<script setup lang="ts">
// The paused schedule's arguments, edited where its alert is (2026-10-26): the full error, one field per argument by its
// kind, then "Save and re-enable" (it runs again on its cron) or "Save only". Admins; the API checks it again.
import { computed, onMounted, reactive, ref } from "vue";
import { fixAlertSchedule, getAlertSchedule } from "@/api/console";
import type { AlertSchedule } from "@/api/types";
import { useApi } from "@/composables/useApi";
import { changedArgs, describeChanges, kindOf, parseArg } from "./scheduleFix";

const props = defineProps<{ alertId: string }>();
const emit = defineEmits<{ done: [message: string]; cancel: [] }>();
const { client } = useApi();
const sched = ref<AlertSchedule | null>(null);
const form = reactive<Record<string, string | boolean>>({});
const loading = ref(true), busy = ref(false), error = ref("");

onMounted(async () => {
  try {
    sched.value = await getAlertSchedule(client, props.alertId);
    for (const [k, v] of Object.entries(sched.value.args)) {
      const kind = kindOf(v);
      if (kind !== "fixed") form[k] = kind === "boolean" ? Boolean(v) : String(v);
    }
  } catch (e) { error.value = (e as { body?: { error?: string } }).body?.error ?? "Couldn't read the schedule from Windmill."; }
  finally { loading.value = false; }
});

const edited = computed(() => Object.fromEntries(Object.entries(form).map(([k, raw]) => [k, parseArg(kindOf(sched.value?.args[k]), raw)])));
const changes = computed(() => (sched.value ? changedArgs(sched.value.args, edited.value) : {}));
const invalid = computed(() => Object.entries(edited.value).filter(([, v]) => v === undefined).map(([k]) => k));

async function save(reEnable: boolean) {
  if (!Object.keys(changes.value).length && !reEnable) return;
  busy.value = true; error.value = "";
  try {
    const out = await fixAlertSchedule(client, props.alertId, changes.value, reEnable);
    emit("done", `Saved ${describeChanges(out.changed) || "no change"}${out.re_enabled ? "; it runs again on its schedule" : ""}.`);
  } catch (e) { error.value = (e as { body?: { error?: string } }).body?.error ?? "Couldn't save the change."; }
  finally { busy.value = false; }
}
</script>

<template>
  <div class="fix" data-test="schedule-fix">
    <p v-if="loading" class="muted small" role="status">Reading the schedule…</p>
    <template v-else-if="sched">
      <p v-if="sched.last_error" class="why small"><strong>Why it stopped</strong><span class="mono">{{ sched.last_error }}</span></p>
      <p class="muted small mono">{{ sched.flow_path }} · {{ sched.schedule }} {{ sched.timezone }}</p>
      <form class="args" @submit.prevent="save(true)">
        <template v-for="(v, k) in sched.args" :key="k">
          <label v-if="kindOf(v) === 'boolean'" class="chk"><input v-model="form[k]" type="checkbox" :aria-label="String(k)" /><span class="mono">{{ k }}</span></label>
          <label v-else-if="kindOf(v) !== 'fixed'"><span class="mono">{{ k }}</span>
            <input v-model="form[k]" class="field" :type="kindOf(v) === 'number' ? 'number' : 'text'" :aria-label="String(k)" /></label>
          <p v-else class="faint small"><span class="mono">{{ k }}</span> stays as it is.</p>
        </template>
        <p v-if="invalid.length" class="err small" role="alert">Give {{ invalid.join(", ") }} a number.</p>
        <div class="row">
          <button type="submit" class="btn btn--sm btn--primary" :disabled="busy || invalid.length > 0">{{ busy ? "Saving…" : "Save and re-enable" }}</button>
          <button type="button" class="btn btn--sm" :disabled="busy || invalid.length > 0 || !Object.keys(changes).length" @click="save(false)">Save only</button>
          <button type="button" class="btn btn--sm btn--ghost" :disabled="busy" @click="emit('cancel')">Cancel</button>
        </div>
      </form>
    </template>
    <p v-if="error" class="err small" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.fix { display: grid; gap: var(--space-3); padding: var(--space-3) 0 0; }
.why { display: grid; gap: var(--space-1); margin: 0; }
.why .mono { white-space: pre-wrap; overflow-wrap: anywhere; color: var(--ink-2); }
.args { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--space-3); align-items: end; }
.args label { display: grid; gap: var(--space-1); font-size: var(--text-xs); min-width: 0; }
.args .chk { display: flex; align-items: center; gap: var(--space-2); }
.args .row, .args .err { grid-column: 1 / -1; }
.row { display: flex; gap: var(--space-2); flex-wrap: wrap; }
.field { width: 100%; min-width: 0; }
.small { font-size: var(--text-xs); }
.muted { color: var(--muted); }
p { margin: 0; }
</style>
